import React, { createContext, useContext, useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { useUser, useAuth } from '@clerk/clerk-react';
import apiClient from '../api/client';
import { getUserId, identifySignedInUser, resetToAnonymousUser, setUserProperties, trackAuthCompleted, trackGuestRewardRedemption } from '../analytics';
import { normalizeUserProfileResponse, redeemRewardClaimTokens, getPendingGuestRewards, syncUserProfileDebounced } from '../api/users';
import { useAlertQueueActions } from './AlertQueueContext';
import { useAppResources } from './AppResourcesContext';
import { queueResourceAlertsFromPayload } from '../utils/resourceAlerts';
import { queueClaimedRewardCelebrations } from '../utils/claimedRewardCelebrations';
import { useResourceCelebration } from './ResourceCelebrationContext';
import { readPendingRewardClaimTokens, storePendingRewardClaimTokens, normalizeRewardClaimTokens } from '../utils/rewardClaims';
import {
  getConsentPreferences,
  mergeServerConsentIfNewer,
} from '../utils/consentPreferences';
import { mergeProfileWithUpdates } from '../utils/profileMerge';

const UserProfileContext = createContext();
const POST_AUTH_RESOURCE_ALERTS_HINT_KEY = 'post-auth-resource-alerts-hint';
const POST_AUTH_LOGIN_EVENT_HINT_KEY = 'post-auth-login-event-hint';
const POST_AUTH_LOGIN_EVENT_PENDING_KEY = 'post-auth-login-event-pending';
const APP_RESOURCE_ALERTS_CURSOR_KEY = 'app-resource-alerts-cursor';
const POST_AUTH_ALERTS_MAX_RETRIES = 2;
const POST_AUTH_ALERTS_RETRY_BASE_DELAY_MS = 800;
const POST_AUTH_ALERTS_WINDOW_MS = 1 * 60 * 1000;
const POST_AUTH_LOGIN_EVENT_WINDOW_MS = 1 * 60 * 1000;
const POST_AUTH_LOGIN_EVENT_MAX_RETRIES = 3;
const POST_AUTH_LOGIN_EVENT_RETRY_BASE_DELAY_MS = 400;
const FOREGROUND_PROFILE_REFRESH_DEBOUNCE_MS = 3000;
const getAlertsCursorStorageKey = (clerkUserId) =>
  `${APP_RESOURCE_ALERTS_CURSOR_KEY}:${clerkUserId || 'unknown'}`;
const isNewerAlertsCursor = (candidate, previous) => {
  const candidateMs = Date.parse(candidate);
  const previousMs = Date.parse(previous);
  if (!Number.isFinite(previousMs)) return true;
  if (candidateMs !== previousMs) return candidateMs > previousMs;
  // Date.parse truncates microseconds; compare the remainder without rounding it away.
  const fraction = (value) => (String(value).match(/\.(\d+)/)?.[1] || '').padEnd(6, '0');
  return fraction(candidate) > fraction(previous);
};
const normalizeAnalyticsProperties = (properties = {}) => (
  Object.fromEntries(
    Object.entries(properties || {}).map(([key, value]) => [key, value === undefined ? null : value])
  )
);
const areAnalyticsValuesEqual = (left, right) => JSON.stringify(left) === JSON.stringify(right);
const DEFINITIVE_REWARD_CLAIM_STATUSES = new Set([
  'redeemed',
  'already_redeemed',
  'ignored',
  'expired',
  'invalid',
  'fulfilled',
]);

/**
 * Hook to access user profile data.
 * @returns {Object} profile data, loading state, and utility functions
 */
export const useUserProfile = () => {
  const context = useContext(UserProfileContext);
  if (!context) {
    throw new Error('useUserProfile must be used within a UserProfileProvider');
  }
  return context;
};

/**
 * UserProfileProvider - Provides centralized user profile state from /api/users/me
 * 
 * Profile data includes:
 * - id, username, email, first_name, last_name
 * - clerk_user_id, account_type
 * - transactional_email_consent, language_level, analytics_consent
 * - newsletter_subscribed_at
 * - app_resources.gettoncini.balance
 * - app_resources.learned_terms.count
 * - created_at, updated_at, last_seen_at
 */
export const UserProfileProvider = ({ children }) => {
  const { isSignedIn, user, isLoaded: userLoaded } = useUser();
  const { getToken, signOut } = useAuth();
  const { appResources, replaceAppResources, updateAppResourcesLocally } = useAppResources();
  
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const prevIsSignedInRef = useRef(isSignedIn);
  const fetchInProgressRef = useRef(false);
  const fetchPromiseRef = useRef(null);
  const inFlightIncludesRecentAlertsRef = useRef(false);
  const inFlightEmitLoginEventRef = useRef(false);
  const pendingRecentAlertsFetchRef = useRef(false);
  const pendingRecentAlertsAllowRetriesRef = useRef(false);
  const pendingEmitLoginFetchRef = useRef(false);
  const pendingEmitLoginHintStorageKeyRef = useRef(null);
  const autoFetchKeyRef = useRef(null);
  const fetchProfileRef = useRef(null);
  const hasProfileDataRef = useRef(false);
  const postAuthRetryTimeoutRef = useRef(null);
  const getTokenRef = useRef(getToken);
  const signOutRef = useRef(signOut);
  const { queueAlert, setAlertAccount } = useAlertQueueActions();
  const { queueResourceCelebration } = useResourceCelebration();
  const queueResourceCelebrationRef = useRef(queueResourceCelebration);
  queueResourceCelebrationRef.current = queueResourceCelebration;
  const accountKey = isSignedIn && user?.id ? `account:${user.id}` : `anonymous:${getUserId() || 'unknown'}`;
  const authEpochRef = useRef({ key: accountKey, epoch: 0 });
  if (authEpochRef.current.key !== accountKey) {
    authEpochRef.current = { key: accountKey, epoch: authEpochRef.current.epoch + 1 };
  }
  const authBoundaryRef = useRef(null);
  const queueAlertRef = useRef(queueAlert);
  const lastForegroundRefreshAtRef = useRef(0);
  const lastAnalyticsPropertiesRef = useRef(null);
  
  // Remembered name from localStorage (for returning anonymous users)
  const [rememberedName, setRememberedName] = useState(() => {
    try {
      const stored = localStorage.getItem('remembered-user-name');
      return stored ? JSON.parse(stored) : null;
    } catch (_) {
      return null;
    }
  });

  useLayoutEffect(() => {
    setAlertAccount?.(accountKey);
    fetchInProgressRef.current = false;
    fetchPromiseRef.current = null;
    inFlightIncludesRecentAlertsRef.current = false;
    inFlightEmitLoginEventRef.current = false;
    pendingRecentAlertsFetchRef.current = false;
    pendingRecentAlertsAllowRetriesRef.current = false;
    pendingEmitLoginFetchRef.current = false;
    pendingEmitLoginHintStorageKeyRef.current = null;
    autoFetchKeyRef.current = null;
    authBoundaryRef.current = null;
    hasProfileDataRef.current = false;
    if (authEpochRef.current.epoch > 0) {
      setProfile(null);
      replaceAppResources(null);
    }
    if (postAuthRetryTimeoutRef.current) clearTimeout(postAuthRetryTimeoutRef.current);
  }, [accountKey, setAlertAccount, replaceAppResources]);

  useEffect(() => {
    getTokenRef.current = getToken;
  }, [getToken]);

  useEffect(() => {
    signOutRef.current = signOut;
  }, [signOut]);

  useEffect(() => {
    queueAlertRef.current = queueAlert;
  }, [queueAlert]);

  useEffect(() => {
    hasProfileDataRef.current = profile !== null;
  }, [profile]);

  useEffect(() => () => {
    authEpochRef.current.epoch += 1;
    if (postAuthRetryTimeoutRef.current) {
      clearTimeout(postAuthRetryTimeoutRef.current);
      postAuthRetryTimeoutRef.current = null;
    }
  }, []);

  const syncAnalyticsPropertiesIfChanged = useCallback((nextProperties) => {
    const normalizedProperties = normalizeAnalyticsProperties(nextProperties);
    const previousProperties = lastAnalyticsPropertiesRef.current;
    const hasChanged = !previousProperties || Object.entries(normalizedProperties).some(
      ([key, value]) => !areAnalyticsValuesEqual(value, previousProperties[key])
    );

    if (!hasChanged) {
      return;
    }

    lastAnalyticsPropertiesRef.current = normalizedProperties;
    setUserProperties(normalizedProperties);
  }, []);

  const getPostAuthHint = useCallback(() => {
    try {
      const raw = sessionStorage.getItem(POST_AUTH_RESOURCE_ALERTS_HINT_KEY);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw);
      const createdAtMs = Number(parsed?.created_at_ms);
      if (!Number.isFinite(createdAtMs)) {
        sessionStorage.removeItem(POST_AUTH_RESOURCE_ALERTS_HINT_KEY);
        return null;
      }
      const ageMs = Date.now() - createdAtMs;
      if (ageMs < 0 || ageMs > POST_AUTH_ALERTS_WINDOW_MS) {
        sessionStorage.removeItem(POST_AUTH_RESOURCE_ALERTS_HINT_KEY);
        return null;
      }
      return parsed;
    } catch (_) {
      return null;
    }
  }, []);

  const isLikelyFreshSignup = useCallback(() => {
    if (!user?.createdAt) return false;
    const createdAt = new Date(user.createdAt);
    if (Number.isNaN(createdAt.getTime())) return false;
    const ageMs = Date.now() - createdAt.getTime();
    return ageMs >= 0 && ageMs <= POST_AUTH_ALERTS_WINDOW_MS;
  }, [user?.createdAt]);

  const shouldHydrateRecentAlerts = useCallback(() => {
    if (getPostAuthHint()) return true;
    return isSignedIn && isLikelyFreshSignup();
  }, [getPostAuthHint, isSignedIn, isLikelyFreshSignup]);

  const getPostAuthLoginHintKey = useCallback(() => {
    if (!isSignedIn) {
      return null;
    }

    const hasFreshHint = (storageKey) => {
      try {
        const raw = sessionStorage.getItem(storageKey);
        if (!raw) {
          return false;
        }
        const parsed = JSON.parse(raw);
        const createdAtMs = Number(parsed?.created_at_ms);
        if (!Number.isFinite(createdAtMs)) {
          sessionStorage.removeItem(storageKey);
          return false;
        }
        const ageMs = Date.now() - createdAtMs;
        if (ageMs < 0 || ageMs > POST_AUTH_LOGIN_EVENT_WINDOW_MS) {
          sessionStorage.removeItem(storageKey);
          return false;
        }
        return true;
      } catch (_) {
        return false;
      }
    };

    if (hasFreshHint(POST_AUTH_LOGIN_EVENT_HINT_KEY)) {
      return POST_AUTH_LOGIN_EVENT_HINT_KEY;
    }

    if (hasFreshHint(POST_AUTH_LOGIN_EVENT_PENDING_KEY)) {
      return POST_AUTH_LOGIN_EVENT_PENDING_KEY;
    }

    try {
      sessionStorage.removeItem(POST_AUTH_LOGIN_EVENT_PENDING_KEY);
    } catch (_) {
    }
    return null;
  }, [isSignedIn]);

  const ensurePostAuthHintForFreshSignup = useCallback(() => {
    if (!isSignedIn || !isLikelyFreshSignup()) {
      return;
    }
    if (getPostAuthHint()) {
      return;
    }
    try {
      sessionStorage.setItem(
        POST_AUTH_RESOURCE_ALERTS_HINT_KEY,
        JSON.stringify({
          reason: 'account_signup',
          created_at_ms: Date.now(),
        }),
      );
    } catch (_) {}
  }, [getPostAuthHint, isLikelyFreshSignup, isSignedIn]);

  /**
   * Fetch user profile from server
   */
  const fetchProfile = useCallback(async (options = {}) => {
    const includeRecentResourceAlerts = options?.includeRecentResourceAlerts === true;
    const emitLoginEvent = options?.emitLoginEvent === true;
    const loginEventHintStorageKey = options?.loginEventHintStorageKey || null;
    const loginEventRetryCount = Number.isFinite(Number(options?.loginEventRetryCount))
      ? Number(options?.loginEventRetryCount)
      : 0;
    const forceFetch = options?.force === true;
    const backgroundRefresh = options?.background === true;
    const allowPostAuthRetries = options?.allowPostAuthRetries === true;
    const retryCount = Number.isFinite(Number(options?.retryCount))
      ? Number(options?.retryCount)
      : 0;
    const fetchOwnerEpoch = authEpochRef.current.epoch;
    const isCurrentAccount = () => fetchOwnerEpoch === authEpochRef.current.epoch;
    const uid = getUserId();
    const signedInUserId = isSignedIn ? (user?.id || null) : null;
    const alertsCursorStorageKey = signedInUserId
      ? getAlertsCursorStorageKey(signedInUserId)
      : null;
    const autoFetchKey = `${isSignedIn ? `signed:${signedInUserId || 'unknown'}` : 'anon'}:${uid || 'no-uid'}:${includeRecentResourceAlerts ? 'recent' : 'base'}:${emitLoginEvent ? 'emit_login' : 'no_emit_login'}`;

    if (!forceFetch && autoFetchKeyRef.current === autoFetchKey) {
      return;
    }
    if (!forceFetch) {
      autoFetchKeyRef.current = autoFetchKey;
    }

    // Prevent concurrent fetches while letting callers await the active request.
    if (fetchInProgressRef.current) {
      if (includeRecentResourceAlerts && !inFlightIncludesRecentAlertsRef.current) {
        pendingRecentAlertsFetchRef.current = true;
        if (allowPostAuthRetries) {
          pendingRecentAlertsAllowRetriesRef.current = true;
        }
      }
      if (emitLoginEvent && !inFlightEmitLoginEventRef.current) {
        pendingEmitLoginFetchRef.current = true;
        if (loginEventHintStorageKey) {
          pendingEmitLoginHintStorageKeyRef.current = loginEventHintStorageKey;
        }
      }
      return fetchPromiseRef.current;
    }
    fetchInProgressRef.current = true;
    inFlightIncludesRecentAlertsRef.current = includeRecentResourceAlerts;
    inFlightEmitLoginEventRef.current = emitLoginEvent;

    const hasExistingProfileData = hasProfileDataRef.current;
    const shouldShowLoading = !(backgroundRefresh && hasExistingProfileData);
    if (shouldShowLoading) {
      setIsLoading(true);
    }
    setError(null);

    const runFetch = async () => {
      let claimedRewardsPayload = null;
      const presentClaimedRewards = ({ hydrateResources = false } = {}) => {
        if (!claimedRewardsPayload) return;
        const payload = claimedRewardsPayload;
        claimedRewardsPayload = null;
        if (hydrateResources && payload.app_resources) {
          updateAppResourcesLocally(payload.app_resources);
        }
        queueClaimedRewardCelebrations({
          queueResourceCelebration: queueResourceCelebrationRef.current,
          payload,
        });
      };
      try {
        const token = await getTokenRef.current({ template: 'basic_user_info_jwt_template' }).catch(() => null);
        if (!isCurrentAccount()) return null;
        const shouldRequestLoginEvent = emitLoginEvent && isSignedIn && Boolean(token);
        let pendingRewardClaimTokens = (isSignedIn && token)
          ? readPendingRewardClaimTokens()
          : [];
        if (isSignedIn && token) {
          try {
            const pendingRewards = await getPendingGuestRewards(token);
            if (!isCurrentAccount()) return null;
            pendingRewardClaimTokens = normalizeRewardClaimTokens([...pendingRewardClaimTokens, ...(pendingRewards?.tokens || [])]);
            storePendingRewardClaimTokens(pendingRewardClaimTokens);
          } catch (_) {
            // Stored tokens remain usable; a later profile refresh retries server discovery.
          }
        }

        if (emitLoginEvent && isSignedIn && !token && loginEventHintStorageKey) {
          const shouldRetryLoginEvent = loginEventRetryCount < POST_AUTH_LOGIN_EVENT_MAX_RETRIES;
          if (shouldRetryLoginEvent) {
            const retryDelayMs = POST_AUTH_LOGIN_EVENT_RETRY_BASE_DELAY_MS * (loginEventRetryCount + 1);
            setTimeout(() => {
              if (!isCurrentAccount()) return;
              const retryPromise = fetchProfileRef.current?.({
                emitLoginEvent: true,
                loginEventHintStorageKey,
                includeRecentResourceAlerts,
                force: true,
                allowPostAuthRetries,
                retryCount,
                loginEventRetryCount: loginEventRetryCount + 1,
              });
              if (retryPromise && typeof retryPromise.catch === 'function') {
                retryPromise.catch(() => {});
              }
            }, retryDelayMs);
          }
        }

        const headers = {};
        if (uid) headers['X-User-ID'] = uid;
        if (token) headers['Authorization'] = `Bearer ${token}`;

        if (pendingRewardClaimTokens.length > 0 && token) {
          try {
            const redemptionResponse = await redeemRewardClaimTokens(pendingRewardClaimTokens, token);
            if (!isCurrentAccount()) return null;
            // Wait for profile hydration so claimed and signup/login awards
            // enter the animation queue together, even on a slow connection.
            claimedRewardsPayload = redemptionResponse;
            const redemptionResults = Array.isArray(redemptionResponse?.results)
              ? redemptionResponse.results
              : [];
            redemptionResults.forEach((result) => trackGuestRewardRedemption?.(result));
            const terminalIndices = new Set(redemptionResults.flatMap((result, index) => (
              DEFINITIVE_REWARD_CLAIM_STATUSES.has(result?.status) ? [result.token_index ?? index] : []
            )));
            storePendingRewardClaimTokens(pendingRewardClaimTokens.filter((_, index) => !terminalIndices.has(index)));
          } catch (redemptionError) {
            trackGuestRewardRedemption?.({ status: 'network_error' });
            console.warn('[UserProfile] Failed to redeem reward claim tokens:', redemptionError);
          }
        }

        if (!isCurrentAccount()) return null;
        const params = {};
        if (shouldRequestLoginEvent) {
          params.emit_login_event = true;
        }
        if (includeRecentResourceAlerts && isSignedIn && token) {
          params.include_recent_resource_alerts = true;
          params.recent_resource_alerts_scope = 'auth';
          if (!authBoundaryRef.current) {
            let hint = getPostAuthHint();
            if (!hint && loginEventHintStorageKey) {
              try { hint = JSON.parse(sessionStorage.getItem(loginEventHintStorageKey)); } catch (_) {}
            }
            const startedAt = Number(hint?.started_at_ms);
            const createdAt = isLikelyFreshSignup() ? new Date(user.createdAt).getTime() : NaN;
            const boundaryMs = Number.isFinite(startedAt) ? startedAt
              : Number.isFinite(createdAt) ? createdAt
                : Date.now() - POST_AUTH_ALERTS_WINDOW_MS;
            authBoundaryRef.current = new Date(boundaryMs).toISOString();
          }
          params.recent_resource_alerts_auth_started_at = authBoundaryRef.current;
          try {
            const cursorRaw = alertsCursorStorageKey
              ? localStorage.getItem(alertsCursorStorageKey)
              : null;
            if (cursorRaw) {
              const parsedCursor = new Date(cursorRaw);
              if (!Number.isNaN(parsedCursor.getTime())) {
                // Preserve backend precision (microseconds) by forwarding raw cursor string.
                params.recent_resource_alerts_since = cursorRaw;
              }
            }
          } catch (_) {}
        }

        const response = await apiClient.get('/users/me', { headers, params });
        if (!isCurrentAccount()) return null;
        const profileData = normalizeUserProfileResponse(response.data);
        const postAuthHint = includeRecentResourceAlerts ? getPostAuthHint() : null;
        const shouldSuppressMondialiSignupGettonciniAlert = (
          postAuthHint?.reason === 'account_signup'
          && postAuthHint?.source_surface === 'mondiali'
        );
        if (
          shouldRequestLoginEvent
          && loginEventHintStorageKey
          && profileData?.account_type === 'registered'
        ) {
          try {
            sessionStorage.removeItem(loginEventHintStorageKey);
          } catch (_) {}
        }
        replaceAppResources(profileData?.app_resources);
        setProfile(profileData);
        presentClaimedRewards();

        if (includeRecentResourceAlerts && isSignedIn && token) {
          const queuedAlerts = queueResourceAlertsFromPayload({
            queueAlert: queueAlertRef.current,
            payload: profileData,
            isAnonymous: false,
            firstAlertDelayMs: 500,
            signedInDurationMs: 10000,
            streakDurationMs: 5000,
            suppressedGettonciniTransactionKeys: shouldSuppressMondialiSignupGettonciniAlert
              ? ['account_signup']
              : [],
          });
          const cursor = profileData?.app_resources?.metadata?.alerts_cursor || null;
          const shouldRetryPostAuthHydration =
            allowPostAuthRetries
            && shouldHydrateRecentAlerts()
            && queuedAlerts === 0
            && !cursor
            && retryCount < POST_AUTH_ALERTS_MAX_RETRIES;
          try {
            if (cursor) {
              const parsedCursor = new Date(cursor);
              if (!Number.isNaN(parsedCursor.getTime())) {
                const cursorOwnerUserId = profileData?.clerk_user_id || signedInUserId;
                if (cursorOwnerUserId) {
                  const storageKey = getAlertsCursorStorageKey(cursorOwnerUserId);
                  const previousCursor = localStorage.getItem(storageKey);
                  if (!previousCursor || isNewerAlertsCursor(cursor, previousCursor)) {
                    localStorage.setItem(storageKey, String(cursor));
                  }
                }
              }
            }
            // Cleanup legacy non-scoped key from previous builds.
            localStorage.removeItem(APP_RESOURCE_ALERTS_CURSOR_KEY);
            if (!shouldRetryPostAuthHydration) {
              sessionStorage.removeItem(POST_AUTH_RESOURCE_ALERTS_HINT_KEY);
            }
          } catch (_) {}
          if (shouldRetryPostAuthHydration) {
            if (postAuthRetryTimeoutRef.current) {
              clearTimeout(postAuthRetryTimeoutRef.current);
            }
            const retryDelay = POST_AUTH_ALERTS_RETRY_BASE_DELAY_MS * (retryCount + 1);
            postAuthRetryTimeoutRef.current = setTimeout(() => {
              if (!isCurrentAccount()) return;
              fetchProfileRef.current?.({
                includeRecentResourceAlerts: true,
                force: true,
                allowPostAuthRetries: true,
                retryCount: retryCount + 1,
              });
            }, retryDelay);
          }
        }

        const localConsentPreferences = getConsentPreferences();
        const localConsentUpdatedAtMs = Date.parse(localConsentPreferences?.updated_at || '');
        const serverConsentUpdatedAtMs = Date.parse(profileData?.consent_updated_at || '');
        const merged = mergeServerConsentIfNewer({
          profile: profileData,
          region: localConsentPreferences?.region || 'eu',
        });

        if (
          !merged.applied &&
          Number.isFinite(localConsentUpdatedAtMs) &&
          (!Number.isFinite(serverConsentUpdatedAtMs) || localConsentUpdatedAtMs > serverConsentUpdatedAtMs)
        ) {
          syncUserProfileDebounced();
        }

        syncAnalyticsPropertiesIfChanged({
          account_type: profileData?.account_type || (isSignedIn ? 'registered' : 'anonymous'),
          language_level: profileData?.language_level || null,
        });
        
        // Mark that this browser has authenticated at least once (if signed in)
        // Also save the user's name to localStorage for use when they're logged out
        if (isSignedIn) {
          try {
            localStorage.setItem('has-ever-authenticated', 'true');
            // Save name for returning anonymous users
            if (profileData?.first_name || profileData?.last_name) {
              const nameData = {
                firstName: profileData.first_name || null,
                lastName: profileData.last_name || null,
              };
              localStorage.setItem('remembered-user-name', JSON.stringify(nameData));
              setRememberedName(nameData);
            }
          } catch (_) {}
        }
        
        // Sync newsletter subscription status to localStorage for components that check localStorage
        try {
          if (profileData?.newsletter_subscribed_at) {
            if (!localStorage.getItem('newsletter-signup-completed')) {
              localStorage.setItem('newsletter-signup-completed', Date.now().toString());
            }
          } else {
            // User is not subscribed according to server - clear localStorage if set
            // (handles case where user unsubscribed on another device)
            if (localStorage.getItem('newsletter-signup-completed')) {
              localStorage.removeItem('newsletter-signup-completed');
            }
          }
        } catch (_) {}

        console.log('[UserProfile] Profile loaded successfully');
        return profileData;
      } catch (err) {
        if (!isCurrentAccount()) return null;
        const status = err?.response?.status;
        
        // Handle 409 Conflict - max 1 authenticated user per device
        if (status === 409) {
          const msg = 'Accesso non riuscito: Massimo di 1 utente autenticato per dispositivo.';
          try {
            await signOutRef.current({ redirectUrl: `/?snackbar=${encodeURIComponent(msg)}&snackbar_type=error&snackbar_duration=5000` });
          } catch (_) {}
          setError(msg);
        } else {
          console.error('[UserProfile] Failed to fetch profile:', err);
          setError(err?.message || 'Failed to fetch profile');
        }
        if (status === 409) {
          claimedRewardsPayload = null;
          setProfile(null);
          replaceAppResources(null);
        }
        return null;
      } finally {
        if (isCurrentAccount()) {
          // A failed profile fetch must not lose successfully redeemed rewards.
          presentClaimedRewards({ hydrateResources: true });
          setIsLoading(false);
          fetchInProgressRef.current = false;
          inFlightIncludesRecentAlertsRef.current = false;
          inFlightEmitLoginEventRef.current = false;
          fetchPromiseRef.current = null;
          if (pendingRecentAlertsFetchRef.current || pendingEmitLoginFetchRef.current) {
            const pendingRecentAlerts = pendingRecentAlertsFetchRef.current;
            const pendingAllowRetries = pendingRecentAlertsAllowRetriesRef.current;
            const pendingEmitLogin = pendingEmitLoginFetchRef.current;
            const pendingLoginEventHintStorageKey = pendingEmitLoginHintStorageKeyRef.current;
            pendingRecentAlertsFetchRef.current = false;
            pendingRecentAlertsAllowRetriesRef.current = false;
            pendingEmitLoginFetchRef.current = false;
            pendingEmitLoginHintStorageKeyRef.current = null;
            const followUpPromise = fetchProfileRef.current?.({
              includeRecentResourceAlerts: pendingRecentAlerts,
              emitLoginEvent: pendingEmitLogin,
              loginEventHintStorageKey: pendingLoginEventHintStorageKey,
              force: true,
              allowPostAuthRetries: pendingAllowRetries,
            });
            if (followUpPromise && typeof followUpPromise.catch === 'function') {
              followUpPromise.catch(() => {});
            }
          }
        }
      }
    };

    fetchPromiseRef.current = runFetch();
    return fetchPromiseRef.current;
  }, [isSignedIn, user?.id, user?.createdAt, getPostAuthHint, isLikelyFreshSignup, shouldHydrateRecentAlerts, syncAnalyticsPropertiesIfChanged, updateAppResourcesLocally]);

  fetchProfileRef.current = fetchProfile;

  /**
   * Refresh profile from server
   */
  const refreshProfile = useCallback((options = {}) => {
    const includeRecentResourceAlerts = options?.includeRecentResourceAlerts === true;
    const backgroundRefresh = options?.background === true;
    return fetchProfile({ includeRecentResourceAlerts, background: backgroundRefresh, force: true });
  }, [fetchProfile]);

  /**
   * Update profile fields locally (optimistic update)
   * Call this after updating profile on server to keep context in sync
   */
  const updateProfileLocally = useCallback((updatesOrUpdater) => {
    if (typeof updatesOrUpdater === 'function') {
      setProfile((prev) => mergeProfileWithUpdates(prev, updatesOrUpdater(prev)));
      return;
    }

    if (updatesOrUpdater && typeof updatesOrUpdater === 'object' && updatesOrUpdater.app_resources) {
      updateAppResourcesLocally(updatesOrUpdater.app_resources);
    }

    setProfile((prev) => mergeProfileWithUpdates(prev, updatesOrUpdater));
  }, [updateAppResourcesLocally]);

  // Fetch profile on mount and when sign-in state changes
  useEffect(() => {
    // Wait for Clerk to load
    if (!userLoaded) return;

    // Ensure analytics identity switches to Clerk as soon as Clerk session is available.
    if (isSignedIn && user?.id) {
      identifySignedInUser(user.id, {
        account_type: 'registered',
      });
    }

    // Fetch profile for both signed-in and anonymous users
    // For signed-in users: uses Authorization header + X-User-ID
    // For anonymous users: uses only X-User-ID header
    if (isSignedIn && user?.id) {
      // Signed-in user - wait for user object to be available
      ensurePostAuthHintForFreshSignup();
      const loginEventHintStorageKey = getPostAuthLoginHintKey();
      const shouldEmitLoginEvent = Boolean(loginEventHintStorageKey);
      const shouldIncludeRecentResourceAlerts = shouldHydrateRecentAlerts() || shouldEmitLoginEvent;
      fetchProfile({
        emitLoginEvent: shouldEmitLoginEvent,
        loginEventHintStorageKey,
        includeRecentResourceAlerts: shouldIncludeRecentResourceAlerts,
        // Normal mount hydration can include recent alerts, but should not trigger retry loops.
        allowPostAuthRetries: false,
      });
    } else if (!isSignedIn) {
      // Anonymous user - fetch profile using X-User-ID
      fetchProfile({ includeRecentResourceAlerts: false });
    }
  }, [
    userLoaded,
    isSignedIn,
    user?.id,
    fetchProfile,
    shouldHydrateRecentAlerts,
    ensurePostAuthHintForFreshSignup,
    getPostAuthLoginHintKey,
  ]);

  useEffect(() => {
    if (!isSignedIn || !user?.id) {
      return undefined;
    }

    const handleVisibilityOrFocus = () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
        return;
      }

      const now = Date.now();
      if (now - lastForegroundRefreshAtRef.current < FOREGROUND_PROFILE_REFRESH_DEBOUNCE_MS) {
        return;
      }
      lastForegroundRefreshAtRef.current = now;
      refreshProfile({ background: true }).catch(() => {});
    };

    document.addEventListener('visibilitychange', handleVisibilityOrFocus);
    window.addEventListener('focus', handleVisibilityOrFocus);
    window.addEventListener('online', handleVisibilityOrFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
      window.removeEventListener('focus', handleVisibilityOrFocus);
      window.removeEventListener('online', handleVisibilityOrFocus);
    };
  }, [isSignedIn, user?.id, refreshProfile]);

  // Also refetch when auth transitions from signed-out to signed-in
  useEffect(() => {
    const wasSignedIn = prevIsSignedInRef.current;
    if (!wasSignedIn && isSignedIn && user?.id) {
      trackAuthCompleted({
        clerk_user_id: user.id,
      });
    }
    if (wasSignedIn && !isSignedIn) {
      if (postAuthRetryTimeoutRef.current) {
        clearTimeout(postAuthRetryTimeoutRef.current);
        postAuthRetryTimeoutRef.current = null;
      }
      resetToAnonymousUser();
      setProfile(null);
      replaceAppResources(null);
      syncAnalyticsPropertiesIfChanged({
        account_type: 'anonymous',
        language_level: null,
      });
      try {
        sessionStorage.removeItem(POST_AUTH_RESOURCE_ALERTS_HINT_KEY);
        sessionStorage.removeItem(POST_AUTH_LOGIN_EVENT_HINT_KEY);
        sessionStorage.removeItem(POST_AUTH_LOGIN_EVENT_PENDING_KEY);
      } catch (_) {}
      pendingEmitLoginFetchRef.current = false;
      pendingEmitLoginHintStorageKeyRef.current = null;
    }
    prevIsSignedInRef.current = isSignedIn;
  }, [isSignedIn, user?.id, replaceAppResources, syncAnalyticsPropertiesIfChanged]);

  // Memoize convenience accessors
  const mergedProfile = useMemo(() => (
    profile ? {
      ...profile,
      app_resources: appResources,
    } : null
  ), [profile, appResources]);

  const contextValue = useMemo(() => ({
    // Core state
    profile: mergedProfile,
    appResources,
    isLoading,
    error,
    
    // Actions
    refreshProfile,
    updateProfileLocally,
    
    // Convenience accessors (commonly used fields)
    firstName: profile?.first_name || null,
    lastName: profile?.last_name || null,
    email: profile?.email || null,
    username: profile?.username || null,
    languageLevel: profile?.language_level || null,
    accountType: profile?.account_type || null,
    
    // Boolean convenience flags
    isNewsletterSubscribed: !!profile?.newsletter_subscribed_at,
    hasEmailConsent: !!profile?.email_contact_consent,
    hasTransactionalEmailConsent: !!profile?.transactional_email_consent,
    hasAnalyticsConsent: !!profile?.analytics_consent,
    
    // Envelope resources (used by GettonciniContext and learned terms UI)
    gettonciniBalance: appResources?.gettoncini?.balance ?? 0,
    learnedTermsCount: appResources?.learned_terms?.count ?? 0,
    stellineBalance: appResources?.stelline?.balance ?? 0,
    
    // Timestamps
    newsletterSubscribedAt: profile?.newsletter_subscribed_at || null,
    createdAt: profile?.created_at || null,
    
    // Remembered name for returning anonymous users
    rememberedName,
  }), [mergedProfile, appResources, isLoading, error, refreshProfile, updateProfileLocally, rememberedName]);

  return (
    <UserProfileContext.Provider value={contextValue}>
      {children}
    </UserProfileContext.Provider>
  );
};
